import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND } from "@/data/content";
import { INDUSTRIES } from "@/data/industries";
import { imageForPath } from "@/data/images";
import SectionBadge from "@/components/site/SectionBadge";
import FrameworkDiagram from "@/components/site/v2/FrameworkDiagram";
import { track } from "@/lib/analytics";
import { publishedBySlug } from "@/data/tools/registry";
import { SOLUTIONS, PROBLEM_SOLUTION, getSolution } from "@/data/solutions";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

/* ------------------------------------------------------------------ */
/* How We Work — Discover → Strategize → Design → Activate → Optimize  */
/* ------------------------------------------------------------------ */

const APPROACH = [
  {
    title: "Discover",
    body: "Understand the market, customer, competition and commercial reality.",
  },
  {
    title: "Strategize",
    body: "Make the critical choices about where to play and how to win.",
  },
  {
    title: "Design",
    body: "Translate strategy into portfolios, propositions, pricing, channels, sales, brands and growth systems.",
  },
  {
    title: "Activate",
    body: "Work alongside teams to take those choices into the market.",
  },
  {
    title: "Optimize",
    body: "Measure business outcomes, learn and continuously improve performance.",
  },
];

export function OurApproach() {
  return (
    <div id="approach">
      <FrameworkDiagram
        testid="approach-section"
        badge="How We Work"
        headline="From Insight to Impact."
        steps={APPROACH.map((s) => ({ name: s.title, desc: s.body }))}
        kicker="Research should not end in insight. Strategy should not end in a deck. Execution should not operate without a commercial objective."
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Why BLEZZINGS — differentiators + the connected-model story         */
/* ------------------------------------------------------------------ */

const DIFFERENTIATORS = [
  {
    title: "Commercial Outcomes First",
    body: "Decisions grounded in revenue, margins and profitable growth.",
  },
  {
    title: "Built for Execution",
    body: "Strategies shaped by operating realities, not just boardroom presentations.",
  },
  {
    title: "One Connected Growth Agenda",
    body: "Intelligence, strategy, brand, sales and digital working together.",
  },
];

const FRAGMENTED = [
  "Market Research Firm",
  "Strategy Consultant",
  "Brand Agency",
  "Marketing Agencies",
  "Sales Team",
];

export function WhyBlezzings() {
  return (
    <section
      id="why-blezzings"
      data-testid="why-blezzings-section"
      className="py-16 md:py-24 bg-[#F2F2F2]"
    >
      <div className={GRID}>
        <SectionBadge label="Why BLEZZINGS" />
        <div className="mt-6 grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-14">
          <div className="col-span-12 md:col-span-7">
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Strategy and execution. Finally connected.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:flex md:flex-col md:justify-end">
            <p className="text-base text-zinc-600 leading-relaxed max-w-md">
              One partner studies the market. Another defines the strategy.
              Agencies execute the marketing. Sales runs its own priorities.
              Each may perform well — yet the business is left to connect the
              pieces. That is where growth breaks down.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 reveal stagger-reveal">
          {DIFFERENTIATORS.map((d, i) => (
            <div
              key={d.title}
              data-testid={`why-card-${i}`}
              className="border-t-2 border-black pt-8"
            >
              <div className="overline">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-serif-display text-2xl leading-tight text-black mt-3">
                {d.title}
              </h3>
              <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed">
                {d.body}
              </p>
            </div>
          ))}
        </div>

        {/* Fragmented partners → one connected system */}
        <div className="mt-14 md:mt-16 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] items-stretch gap-6 lg:gap-8 reveal">
          <div
            data-testid="market-gap-fragmented"
            className="border border-black/10 bg-white p-7 md:p-8"
          >
            <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-zinc-500">
              The Fragmented Model
            </div>
            <ul className="mt-6 space-y-3">
              {FRAGMENTED.map((f) => (
                <li
                  key={f}
                  className="border border-dashed border-zinc-300 px-4 py-3 text-sm text-zinc-600"
                >
                  {f}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-[13px] text-zinc-500 leading-relaxed">
              Separate partners. Separate priorities. The business connects
              the pieces.
            </p>
          </div>

          <div className="flex items-center justify-center" aria-hidden>
            <ArrowRight className="h-8 w-8 text-zinc-400 rotate-90 lg:rotate-0" />
          </div>

          <div
            data-testid="market-gap-connected"
            className="bg-[#050505] text-white p-7 md:p-8 flex flex-col"
          >
            <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-white/50">
              The BLEZZINGS Model
            </div>
            <div className="mt-6 flex-1 border border-white/25 px-5 py-6 flex flex-col justify-center">
              <div className="font-serif-display text-2xl md:text-3xl leading-tight">
                One Connected Commercial System
              </div>
              <p className="mt-4 text-sm text-white/70 leading-relaxed">
                Market truth → strategic choices → activation → measurable
                business outcomes.
              </p>
            </div>
            <p className="mt-6 text-[13px] text-white/60 leading-relaxed">
              One partner, accountable for the whole commercial journey.
            </p>
          </div>
        </div>

        <p
          data-testid="why-kicker"
          className="mt-12 md:mt-14 font-serif-display text-xl md:text-2xl italic text-black leading-snug"
        >
          One partner. One connected growth agenda.
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Problems We Solve                                                   */
/* ------------------------------------------------------------------ */

/* Five focused consulting solutions — compact strip between Problems and Expertise. */
const SOLUTION_CARD_BLURB = {
  growth360: "Find what is holding growth back — and the moves worth investing in.",
  profitshield: "Find margin erosion across prices, discounts, products and channels.",
  salesmax: "Make the sales system work harder — coverage, conversion, execution.",
  channelmax: "Distribution that performs — partner economics, coverage, sell-through.",
  commercemax: "Online growth that stays profitable — order economics, acquisition, retention.",
};

export function FocusedSolutions() {
  return (
    <section
      id="focused-solutions"
      data-testid="focused-solutions-section"
      className="py-16 md:py-24 bg-[#fafafa] border-y border-black/5"
    >
      <div className={GRID}>
        <SectionBadge label="Focused Solutions" />
        <div className="mt-6 grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-14">
          <div className="col-span-12 md:col-span-7">
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Five focused ways to unlock profitable growth
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:flex md:flex-col md:justify-end">
            <p className="text-base text-zinc-600 leading-relaxed max-w-lg">
              Structured consulting engagements, each built around one commercial
              constraint — diagnosis, priorities and a roadmap you can act on.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5 reveal stagger-reveal">
          {SOLUTIONS.map((s, i) => (
            <div key={s.id} className="group flex flex-col bg-white border border-zinc-200 p-6 transition-all duration-500 hover:border-black">
              <span className="overline">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-serif-display text-xl leading-tight text-black mt-4">
                {s.name}
              </h3>
              <div className="text-[11px] font-medium tracking-wide text-zinc-500 mt-1 leading-snug">
                {s.descriptor}
              </div>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed flex-1">
                {SOLUTION_CARD_BLURB[s.id]}
              </p>
              <Link
                to={`/solutions/${s.id}`}
                data-testid={`home-solution-link-${s.id}`}
                onClick={() => track("solution_card_click", { solution_id: s.id, source_page: "home" })}
                className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-black underline underline-offset-4 decoration-zinc-300 hover:decoration-black transition-colors w-fit"
              >
                Explore solution
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PROBLEMS = [
  {
    title: "Growth Has Stalled",
    body: "Sales have plateaued, growth is slowing or the next engine of growth is unclear.",
    to: "/capabilities/strategy-commercial-transformation/growth-strategy",
    image: "/photos/home-problem-1.jpg",
  },
  {
    title: "Profitability Is Under Pressure",
    body: "Revenue may be rising while margins decline — across pricing, mix, channel economics and trade spend.",
    to: "/capabilities/strategy-commercial-transformation/pricing-revenue-management",
    image: "/photos/home-problem-2.jpg",
  },
  {
    title: "Sales & Distribution Underperform",
    body: "Distributors, dealers, key accounts or sales teams are not delivering their potential.",
    to: "/capabilities/strategy-commercial-transformation/channel-route-to-market",
    image: "/photos/home-problem-3.jpg",
  },
  {
    title: "New Markets Are Difficult to Enter",
    body: "Entering a new geography, category or product involves commercial risk that needs validating first.",
    to: "/capabilities/strategy-commercial-transformation/go-to-market-market-entry",
    image: "/photos/home-problem-4.jpg",
  },
  {
    title: "Marketing Isn't Driving Sales",
    body: "Brand and marketing investments are not creating enough demand, preference, leads or sales.",
    to: "/capabilities/brand-marketing-communications/marketing-strategy-integrated-communications",
    image: "/photos/home-problem-5.jpg",
  },
  {
    title: "Digital Growth Isn't Profitable",
    body: "Acquisition costs are rising, conversions are weak, marketplaces underperform or retention is low.",
    to: "/capabilities/digital-commerce/ecommerce-marketplace-growth",
    image: "/photos/home-problem-6.jpg",
  },
];

const PROBLEM_TOOLS = {
  "Growth Has Stalled": "growth-bottleneck-diagnostic",
  "Profitability Is Under Pressure": "profit-leakage-calculator",
  "Sales & Distribution Underperform": "sales-productivity-calculator",
  "New Markets Are Difficult to Enter": "market-entry-readiness",
  "Marketing Isn't Driving Sales": "marketing-waste-detector",
  "Digital Growth Isn't Profitable": "ecommerce-profitability-calculator",
};

export function ProblemsWeSolve() {
  return (
    <section
      id="problems-we-solve"
      data-testid="problems-we-solve-section"
      className="py-16 md:py-24 bg-white"
    >
      <div className={GRID}>
        <SectionBadge label="Problems We Solve" />
        <div className="mt-6 grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-14">
          <div className="col-span-12 md:col-span-6">
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              What&rsquo;s holding your business back?
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:flex md:flex-col md:justify-end">
            <p className="text-base text-zinc-600 leading-relaxed max-w-lg">
              Growth challenges rarely exist in isolation — what looks like a
              sales problem may originate in pricing, portfolio, positioning
              or distribution. Find yours. Explore the capability behind it.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 reveal stagger-reveal">
          {PROBLEMS.map((p, i) => (
            <div key={p.title}>
              <Link
                to={p.to}
                data-testid={`problem-card-${i}`}
                onClick={() => track("problem_card_open", { problem: p.title })}
                className="group relative flex flex-col h-full bg-white border border-zinc-200 p-8 transition-all duration-500 hover:border-black hover:-translate-y-1"
              >
                {(p.image || imageForPath(p.to)) && (
                  <div className="-mx-8 -mt-8 mb-6 aspect-[16/10] overflow-hidden bg-zinc-200">
                    <img
                      src={p.image || imageForPath(p.to)}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="flex items-baseline justify-between gap-4">
                  <span className="overline">{String(i + 1).padStart(2, "0")}</span>
                  <ArrowUpRight className="h-5 w-5 text-zinc-300 transition-all duration-300 group-hover:text-black group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
                <h3 className="font-serif-display text-2xl leading-tight text-black mt-5">
                  {p.title}
                </h3>
                <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed flex-1">
                  {p.body}
                </p>
                <span
                  aria-hidden
                  className="absolute left-0 bottom-0 h-[3px] w-0 bg-black transition-all duration-500 group-hover:w-full"
                />
              </Link>
              {(PROBLEM_SOLUTION[p.title] || []).length > 0 && (
                <p className="mt-2 text-xs text-zinc-500 px-1">
                  Focused solution:{" "}
                  {PROBLEM_SOLUTION[p.title].map((sid, j) => {
                    const s = getSolution(sid);
                    return (
                      <span key={sid}>
                        {j > 0 && <span className="text-zinc-300"> · </span>}
                        <Link
                          to={`/solutions/${sid}`}
                          data-testid={`problem-solution-link-${sid}`}
                          onClick={() => track("solution_card_click", { solution_id: sid, source_page: "home-problems" })}
                          className="text-black font-medium underline underline-offset-4 decoration-zinc-300 hover:decoration-black transition-colors"
                        >
                          {s.name}
                        </Link>
                      </span>
                    );
                  })}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Diagnose it first — a free tool for each challenge (published only) */}
        {(() => {
          const items = PROBLEMS.map((p) => ({ problem: p, tool: publishedBySlug(PROBLEM_TOOLS[p.title]) })).filter((x) => x.tool);
          if (!items.length) return null;
          return (
            <div className="mt-8 border border-zinc-200 bg-[#fafafa] p-6 md:p-7" data-testid="problem-tools-strip">
              <span className="overline inline-block mt-1">Diagnose it first — free tools</span>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                {items.map(({ problem, tool }) => (
                  <Link
                    key={tool.id}
                    to={tool.url}
                    data-testid={`problem-tool-${tool.id}`}
                    onClick={() => track("problem_tool_click", { tool: tool.id })}
                    className="text-sm font-medium underline underline-offset-4 hover:text-zinc-600 transition-colors"
                  >
                    {problem.title} → {tool.name}
                  </Link>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Industries We Serve (homepage)                                      */
/* ------------------------------------------------------------------ */

export function HomeIndustries() {
  return (
    <section
      id="industries"
      data-testid="home-industries-section"
      className="py-16 md:py-24 bg-white"
    >
      <div className={GRID}>
        <SectionBadge label="Industries We Serve" />
        <div className="mt-6 grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-14">
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Growth expertise across industries.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 reveal stagger-reveal">
          {INDUSTRIES.map((ind, i) => (
            <Link
              key={ind.slug}
              to={`/industries/${ind.slug}`}
              data-testid={`home-industry-${i}`}
              onClick={() => track("home_industry_open", { industry: ind.slug })}
              className="group relative bg-[#F2F2F2] border border-black/5 overflow-hidden transition-all duration-500 hover:border-black hover:-translate-y-1"
            >
              <div className="relative h-28 overflow-hidden bg-zinc-200">
                <img
                  src={ind.image}
                  alt={ind.imageAlt || ind.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover img-edit"
                />
              </div>
              <div className="p-6 flex items-end justify-between gap-4 min-h-[104px]">
                <div>
                  <span className="overline">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-serif-display text-xl leading-snug text-black mt-2">
                    {ind.name}
                  </h3>
                </div>
                <ArrowUpRight className="h-5 w-5 shrink-0 text-zinc-400 transition-all duration-300 group-hover:text-black group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
              <span
                aria-hidden
                className="absolute left-0 bottom-0 h-[3px] w-0 bg-black transition-all duration-500 group-hover:w-full"
              />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Leadership (homepage, concise)                                      */
/* ------------------------------------------------------------------ */

export function HomeLeadership() {
  return (
    <section
      id="home-leadership"
      data-testid="home-leadership-section"
      className="py-16 md:py-24 bg-[#F2F2F2]"
    >
      <div className={GRID}>
        {/* Heading above the image */}
        <div className="mb-12 md:mb-14 reveal">
          <SectionBadge label="Leadership" />
          <h2 className="font-serif-display text-4xl sm:text-5xl mt-6 leading-[1.05]">
            Executive thinking. <br />
            Hands-on execution.
          </h2>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-12 items-center reveal">
          {/* Portrait */}
          <div className="col-span-12 md:col-span-5 relative">
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-zinc-200 border border-zinc-300">
              <img
                data-testid="home-leadership-portrait"
                src={BRAND.ceoPhoto}
                alt="Khalid Kamal Rumi — Founder & CEO, BLEZZINGS"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover object-center slow-scale"
              />
            </div>
            <div
              className="hidden md:block absolute -z-10 -left-6 top-16 w-32 h-32 border border-black"
              aria-hidden
            />
          </div>

          {/* Copy */}
          <div className="col-span-12 md:col-span-7">
            <p className="text-lg text-zinc-600 leading-relaxed max-w-xl">
              BLEZZINGS is built around senior, hands-on involvement across
              strategy and execution.
            </p>
            <p className="mt-4 text-zinc-600 leading-relaxed max-w-xl">
              Our leadership experience spans P&amp;L management, sales,
              marketing, category building, digital commerce, business
              transformation and consulting — giving us an operator&apos;s
              perspective on what it takes to turn strategy into measurable
              commercial performance.
            </p>
            <Link
              to="/leadership"
              data-testid="home-leadership-cta"
              onClick={() => track("leadership_page_open", { source: "home" })}
              className="group mt-8 inline-flex items-center gap-3 h-12 px-7 bg-black text-white text-[15px] font-medium hover:bg-zinc-800 transition-colors duration-300"
            >
              Meet Our Leadership
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
